import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, fmtDate, fmtDateTime } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ShieldCheck, Users2, Contact, Pencil, Ban, CheckCircle2, Megaphone, Landmark, Upload, Download, Trash2, ArrowRight, ChevronUp, ChevronDown, ArrowUpDown } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { SearchableSelect } from "@/components/SearchableSelect";
import { ExportDialog, DATE_FIELDS_BASE, USER_EXPORT_FIELDS } from "@/components/ExportDialog";
import { useExports } from "@/context/ExportsContext";
import { AccessLogs } from "@/components/AccessLogs";

const USER_IMPORT_FIELDS = [
  { key: "name", label: "Nome *" },
  { key: "email", label: "Email *" },
  { key: "role", label: "Ruolo (opzionale)" },
  { key: "cellulare", label: "Cellulare" },
  { key: "telefono_fisso", label: "Telefono fisso" },
  { key: "citta_residenza", label: "Città" },
  { key: "provincia", label: "Provincia" },
  { key: "codice_convenzione", label: "Codice convenzione" },
  { key: "utenze_finanziarie", label: "Utenze finanziarie" },
  { key: "referente", label: "Collaboratore/Referente" },
  { key: "stato", label: "Stato (Attivo/Disattivato)" },
];

const ROLE_ICON = { admin: ShieldCheck, collaboratore: Users2, convenzionato: Contact, segnalatore: Megaphone, istituto: Landmark };
const EMPTY = { email: "", email_comunicazioni: "", password: "", name: "", role: "convenzionato", sede_id: "none", cellulare: "", citta_residenza: "", provincia: "", telefono_fisso: "", codice_convenzione: "", utenze_finanziarie: "", referente_id: "none", percentuale_provvigione: "", email_extra: "" };
const splitEmails = (s) => [...new Set((s || "").split(/[,;\s]+/).map((x) => x.trim().toLowerCase()).filter(Boolean))];

const LastLogin = ({ iso }) => {
  if (!iso) return <span className="text-xs text-muted-foreground">Mai</span>;
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  const rel = days <= 0 ? "oggi" : days === 1 ? "ieri" : `${days} giorni fa`;
  const cls = days > 30 ? "text-rose-600 dark:text-rose-400" : days > 7 ? "text-amber-600 dark:text-amber-400" : "text-emerald-600 dark:text-emerald-400";
  return (
    <span className="text-xs" title={fmtDateTime(iso)}>
      <span className={`font-semibold ${cls}`}>{rel}</span>
      <span className="block text-[11px] text-muted-foreground">{fmtDateTime(iso)}</span>
    </span>
  );
};

export default function Users() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [users, setUsers] = useState([]);
  const [sedi, setSedi] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [fRole, setFRole] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fSede, setFSede] = useState("all");
  const [fColl, setFColl] = useState("all");

  const [importOpen, setImportOpen] = useState(false);
  const [importRole, setImportRole] = useState("convenzionato");
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importColumns, setImportColumns] = useState([]);
  const [importMapping, setImportMapping] = useState({});
  const [exportOpen, setExportOpen] = useState(false);
  const [sel, setSel] = useState([]);
  const [bulkDelOpen, setBulkDelOpen] = useState(false);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "name", dir: "asc" });

  const load = useCallback(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);
  useEffect(() => { load(); api.get("/sedi").then((r) => setSedi(r.data)).catch(() => {}); }, [load]);

  const { startExport } = useExports();
  const onExport = (params) => startExport("users_export", params, "Utenti/Anagrafiche");

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true); setImportResult(null); setImportPreview(null);
    const fd = new FormData(); fd.append("file", file); fd.append("role", importRole);
    try {
      const { data } = await api.post("/users/import-preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportPreview(data); setImportFile(file);
      setImportColumns(data.columns || []); setImportMapping(data.mapping || {});
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore lettura file"); }
    setImporting(false); e.target.value = "";
  };

  const remap = async (field, col) => {
    const nm = { ...importMapping };
    if (col === "none") delete nm[field]; else nm[field] = col;
    setImportMapping(nm);
    if (!importFile) return;
    setImporting(true);
    const fd = new FormData(); fd.append("file", importFile); fd.append("role", importRole); fd.append("mapping", JSON.stringify(nm));
    try {
      const { data } = await api.post("/users/import-preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportPreview(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore anteprima"); }
    setImporting(false);
  };

  const confirmImport = async () => {
    if (!importFile) return;
    setImporting(true);
    const fd = new FormData(); fd.append("file", importFile); fd.append("role", importRole); fd.append("mapping", JSON.stringify(importMapping));
    try {
      const { data } = await api.post("/users/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data); setImportPreview(null); setImportFile(null); setImportColumns([]); setImportMapping({});
      toast.success(`Import: ${data.created} nuovi, ${data.updated} aggiornati${data.skipped ? `, ${data.skipped} ignorati` : ""}`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore import"); }
    setImporting(false);
  };

  const sedeName = useCallback((id) => sedi.find((s) => s.id === id)?.nome || "—", [sedi]);
  const userName = useCallback((id) => users.find((u) => u.id === id)?.name, [users]);

  const openNew = () => { setEditing(null); setForm({ ...EMPTY, referente_id: isAdmin ? "none" : (me?.id || "none") }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ email: u.email, email_comunicazioni: u.email_comunicazioni || u.email, password: "", name: u.name, role: u.role, sede_id: u.sede_id || "none", cellulare: u.cellulare || "", citta_residenza: u.citta_residenza || "", provincia: u.provincia || "", telefono_fisso: u.telefono_fisso || "", codice_convenzione: u.codice_convenzione || "", utenze_finanziarie: u.utenze_finanziarie || "", referente_id: u.referente_id || "none", percentuale_provvigione: u.percentuale_provvigione ?? "", email_extra: (u.email_notifiche_extra || []).join(", "), romulus_extension: u.romulus_extension || "" }); setOpen(true); };

  const save = async () => {
    try {
      const sede_id = form.sede_id === "none" ? "" : form.sede_id;
      const referente_id = form.referente_id === "none" ? "" : form.referente_id;
      if (editing) {
        const payload = { name: form.name, email_comunicazioni: (form.email_comunicazioni || "").trim().toLowerCase() || undefined, role: form.role, sede_id, referente_id, cellulare: form.cellulare, citta_residenza: form.citta_residenza, provincia: form.provincia, telefono_fisso: form.telefono_fisso, codice_convenzione: form.codice_convenzione, utenze_finanziarie: form.utenze_finanziarie, email_notifiche_extra: splitEmails(form.email_extra) };
        if (isAdmin) payload.percentuale_provvigione = form.percentuale_provvigione === "" ? 0 : parseFloat(form.percentuale_provvigione);
        if (form.password) payload.password = form.password;
        if (isAdmin && form.email && form.email.trim().toLowerCase() !== editing.email) payload.email = form.email.trim().toLowerCase();
        await api.put(`/users/${editing.id}`, payload);
        if (isAdmin && (form.romulus_extension || "") !== (editing.romulus_extension || "")) await api.put(`/telefonia/extension/${editing.id}`, { extension: form.romulus_extension });
        toast.success("Utente aggiornato");
      } else {
        if (!form.email || !form.password || !form.name) { toast.error("Compila tutti i campi"); return; }
        const { data } = await api.post("/users", { ...form, email_extra: undefined, email_notifiche_extra: splitEmails(form.email_extra), email_comunicazioni: form.email, sede_id, referente_id, percentuale_provvigione: form.percentuale_provvigione === "" ? undefined : parseFloat(form.percentuale_provvigione) });
        toast.success(data.email !== form.email.toLowerCase() ? `Utente creato · accesso: ${data.email}` : "Utente creato");
      }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleActive = async (u) => {
    if (u.active) await api.delete(`/users/${u.id}`);
    else await api.put(`/users/${u.id}`, { active: true });
    load(); toast.success(u.active ? "Utente disattivato" : "Utente riattivato");
  };

  const [delUser, setDelUser] = useState(null);
  const confirmDelete = async () => {
    if (!delUser) return;
    try { await api.delete(`/users/${delUser.id}/permanent`); toast.success("Utente eliminato definitivamente"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore eliminazione"); }
    setDelUser(null);
  };

  const filtered = useMemo(() => users.filter((u) => {
    if (fRole !== "all" && u.role !== fRole) return false;
    if (fStatus === "active" && u.active === false) return false;
    if (fStatus === "disabled" && u.active !== false) return false;
    if (fSede !== "all") {
      if (fSede === "none" && u.sede_id) return false;
      if (fSede !== "none" && u.sede_id !== fSede) return false;
    }
    if (fColl !== "all") {
      if (fColl === "none" && u.referente_id) return false;
      if (fColl !== "none" && u.referente_id !== fColl && u.id !== fColl) return false;
    }
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      const hay = [u.name, u.email, u.email_comunicazioni, u.cellulare, u.telefono_fisso, ROLE_LABELS[u.role], sedeName(u.sede_id)].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  }), [users, fRole, fStatus, fSede, fColl, q, sedeName]);

  const sortVal = useCallback((u, key) => {
    if (key === "role") return (ROLE_LABELS[u.role] || u.role || "").toLowerCase();
    if (key === "sede") return (sedeName(u.sede_id) || "").toLowerCase();
    if (key === "status") return u.active === false ? 0 : 1;
    if (key === "email") return (u.email || "").toLowerCase();
    if (key === "last_login") return u.last_login_at || "";
    return (u.name || "").toLowerCase();
  }, [sedeName]);
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const va = sortVal(a, sort.key), vb = sortVal(b, sort.key);
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filtered, sort, sortVal]);
  const toggleSort = (key) => setSort((p) => p.key === key ? { key, dir: p.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  const SortHead = ({ label, k, align }) => (
    <th className={`px-4 py-3 font-semibold ${align === "right" ? "text-right" : ""}`}>
      <button className="inline-flex items-center gap-1 hover:text-foreground uppercase tracking-wider" data-testid={`users-sort-${k}`} onClick={() => toggleSort(k)}>
        {label}
        {sort.key === k ? (sort.dir === "asc" ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
      </button>
    </th>
  );

  const selIds = filtered.filter((u) => u.id !== me.id).map((u) => u.id);
  const allSel = selIds.length > 0 && selIds.every((id) => sel.includes(id));
  const toggleSel = (id) => setSel((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const toggleAllSel = () => setSel(allSel ? [] : selIds);
  const bulkDelete = async () => {
    try {
      const { data } = await api.post("/users/bulk-delete", { ids: sel });
      toast.success(`${data.deleted} utenti eliminati`);
      setSel([]); setBulkDelOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore eliminazione"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4" data-testid="users-header">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Gestione Utenti</h1>
          <p className="text-sm text-muted-foreground mt-1">{filtered.length} di {users.length} utenti · gestisci ruoli e accessi</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (<>
          <Button variant="outline" data-testid="users-export-btn" onClick={() => setExportOpen(true)}><Download className="h-4 w-4 mr-2" />Esporta</Button>
          <ExportDialog open={exportOpen} onOpenChange={setExportOpen} title="Esporta Utenti/Anagrafiche" fields={USER_EXPORT_FIELDS} dateFields={DATE_FIELDS_BASE} onConfirm={onExport} />
          <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) { setImportResult(null); setImportPreview(null); setImportFile(null); setImportColumns([]); setImportMapping({}); } }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="users-import-btn"><Upload className="h-4 w-4 mr-2" />Importa</Button>
            </DialogTrigger>
            <DialogContent className={importPreview ? "max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" : "max-w-md"}>
              <DialogHeader><DialogTitle>Importa Utenti (CSV/Excel)</DialogTitle></DialogHeader>
              {!importPreview ? (
              <div className="space-y-4 py-2">
                <p className="text-sm text-muted-foreground">Carica un file con colonne <strong>Nome</strong> ed <strong>Email</strong> (obbligatorie). Colonne opzionali: Cellulare, Telefono fisso, Città, Provincia, Codice convenzione, Utenze finanziarie, <strong>Stato</strong> (Attivo/Disattivato). Se un utente con lo stesso <strong>Nome</strong> esiste già, i suoi dati vengono <strong>aggiornati</strong> (i duplicati all'interno del file vengono ignorati). L'email del file diventa l'<strong>email di comunicazione</strong> (condivisibile); l'<strong>accesso</strong> viene generato aggiungendo un numero se l'indirizzo è già usato (es. mario.rossi2@…). A tutti i nuovi utenti viene assegnata la password temporanea <strong>12345678</strong>, da cambiare al primo accesso. Nessuna email viene inviata agli utenti.</p>
                <div className="space-y-1.5">
                  <Label>Ruolo di default</Label>
                  <Select value={importRole} onValueChange={setImportRole}>
                    <SelectTrigger data-testid="import-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="collaboratore">Collaboratore Interno</SelectItem>
                      <SelectItem value="convenzionato">Convenzionato</SelectItem>
                      <SelectItem value="segnalatore">Segnalatore</SelectItem>
                      <SelectItem value="istituto">Istituto Finanziario</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground">Usato quando la riga non specifica una colonna "Ruolo".</p>
                </div>
                <input type="file" accept=".xlsx,.xls,.csv" id="users-import-file" data-testid="users-import-file" className="hidden" onChange={doImport} />
                <Button className="w-full" disabled={importing} data-testid="users-import-file-btn" onClick={() => document.getElementById("users-import-file").click()}>
                  <Upload className="h-4 w-4 mr-2" />{importing ? "Lettura file..." : "Seleziona file e anteprima"}
                </Button>
                {importResult && (
                  <div className="rounded-md border border-border p-3 text-sm space-y-2" data-testid="import-result">
                    <p>{importResult.created} creati · {importResult.updated} aggiornati · {importResult.skipped} ignorati</p>
                    {importResult.created > 0 && (
                      <p className="text-xs text-muted-foreground">I nuovi utenti accedono con la password temporanea <strong className="font-mono">12345678</strong> e dovranno cambiarla al primo accesso.</p>
                    )}
                  </div>
                )}
              </div>
              ) : (
              <div className="flex flex-col min-h-0 flex-1">
                <div className="flex flex-wrap items-center gap-3 py-2 text-sm" data-testid="users-import-preview-summary">
                  <span className="font-medium">{importPreview.total} righe rilevate</span>
                  <span className="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="h-4 w-4" />{importPreview.new} nuovi</span>
                  <span className="inline-flex items-center gap-1.5 text-amber-600 dark:text-amber-400">{importPreview.update} da aggiornare</span>
                  <span className="inline-flex items-center gap-1.5 text-rose-600 dark:text-rose-400">{importPreview.skip} ignorati</span>
                </div>
                <div className="rounded-lg border border-border p-3 mb-2" data-testid="users-import-mapping">
                  <p className="text-xs text-muted-foreground mb-2">Associa ogni campo alla colonna del tuo file. La mappatura automatica è precompilata: modificala se non è corretta.</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {USER_IMPORT_FIELDS.map((f) => (
                      <div key={f.key} className="flex items-center gap-2">
                        <span className="text-xs w-1/2 truncate" title={f.label}>{f.label}</span>
                        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <Select value={importMapping[f.key] || "none"} onValueChange={(v) => remap(f.key, v)}>
                          <SelectTrigger className="flex-1 h-8" data-testid={`users-map-${f.key}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— nessuna —</SelectItem>
                            {importColumns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="overflow-auto border border-border rounded-lg flex-1 min-h-0">
                  <table className="w-full text-xs">
                    <thead className="bg-muted/50 border-b border-border sticky top-0 z-10">
                      <tr>
                        {["#", "Stato import", "Nome", "Email comunicazioni", "Accesso", "Ruolo", "Cellulare", "Città", "Prov.", "Tel. fisso", "Cod. conv.", "Utenze", "Referente", "Attivo?"].map((h) => (
                          <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.rows.map((r, i) => {
                        const cls = r.status === "skip" ? "bg-rose-50 dark:bg-rose-950/30" : r.status === "update" ? "bg-amber-50 dark:bg-amber-950/30" : "";
                        const badge = r.status === "skip" ? "Ignorato" : r.status === "update" ? "Aggiorna" : "Nuovo";
                        const bcls = r.status === "skip" ? "text-rose-600 dark:text-rose-400" : r.status === "update" ? "text-amber-600 dark:text-amber-400" : "text-emerald-600 dark:text-emerald-400";
                        return (
                          <tr key={i} data-testid={`users-import-row-${i}`} className={`border-b border-border last:border-0 ${cls}`}>
                            <td className="px-3 py-2 text-muted-foreground">{i + 1}</td>
                            <td className={`px-3 py-2 font-medium whitespace-nowrap ${bcls}`} title={r.reason}>{badge}</td>
                            <td className="px-3 py-2 whitespace-nowrap max-w-[160px] truncate" title={r.name}>{r.name || "—"}</td>
                            <td className="px-3 py-2 whitespace-nowrap max-w-[200px] truncate" title={r.email}>{r.email || "—"}</td>
                            <td className="px-3 py-2 whitespace-nowrap max-w-[200px] truncate font-mono text-[11px]" title={r.login}>{r.login || "—"}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{ROLE_LABELS[r.role] || r.role}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{r.cellulare || "—"}</td>
                            <td className="px-3 py-2 whitespace-nowrap max-w-[120px] truncate" title={r.citta_residenza}>{r.citta_residenza || "—"}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{r.provincia || "—"}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{r.telefono_fisso || "—"}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{r.codice_convenzione || "—"}</td>
                            <td className="px-3 py-2 whitespace-nowrap max-w-[120px] truncate" title={r.utenze_finanziarie}>{r.utenze_finanziarie || "—"}</td>
                            <td className={`px-3 py-2 whitespace-nowrap max-w-[140px] truncate ${r.referente_found === false ? "text-amber-600 dark:text-amber-400" : ""}`} title={r.referente_found === false ? `Collaboratore "${r.referente}" non trovato` : r.referente}>{r.referente ? (r.referente_found === false ? `${r.referente} (?)` : r.referente) : "—"}</td>
                            <td className={`px-3 py-2 whitespace-nowrap font-medium ${r.active ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"}`}>{r.active ? "Attivo" : "Disattivato"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <DialogFooter className="pt-3">
                  <Button variant="outline" data-testid="users-import-cancel" onClick={() => { setImportPreview(null); setImportFile(null); setImportColumns([]); setImportMapping({}); }}>Annulla</Button>
                  <Button data-testid="users-import-confirm" disabled={importing || importPreview.new + importPreview.update === 0} onClick={confirmImport}>
                    <CheckCircle2 className="h-4 w-4 mr-2" />{importing ? "Importazione..." : `Conferma e importa (${importPreview.new + importPreview.update})`}
                  </Button>
                </DialogFooter>
              </div>
              )}
            </DialogContent>
          </Dialog>
          </>)}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-btn" onClick={openNew}><Plus className="h-4 w-4 mr-2" /> Nuovo Utente</Button>
            </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Modifica Utente" : "Nuovo Utente"}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5"><Label>Nome completo</Label><Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>{editing ? "Email comunicazioni" : "Email"}</Label><Input data-testid="user-email-input" type="email" value={editing ? (form.email_comunicazioni || form.email) : form.email} onChange={(e) => setForm({ ...form, [editing ? "email_comunicazioni" : "email"]: e.target.value })} />{!editing && <p className="text-[11px] text-muted-foreground">Se l'email è già usata, l'accesso verrà creato aggiungendo un numero (es. mario.rossi2@…). Più utenti possono condividere la stessa email di comunicazione.</p>}{editing && !isAdmin && <p className="text-[11px] text-muted-foreground">Email di accesso: <span className="font-mono">{form.email}</span></p>}</div>
              {editing && isAdmin && <div className="space-y-1.5"><Label>Email di accesso (login)</Label><Input data-testid="user-login-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /><p className="text-[11px] text-muted-foreground">Usata per entrare nel gestionale. Non può coincidere con quella di un altro utente.</p></div>}
              {form.role === "convenzionato" && <div className="space-y-1.5 col-span-2"><Label>Altre email per le notifiche</Label><Input data-testid="user-email-extra-input" value={form.email_extra} onChange={(e) => setForm({ ...form, email_extra: e.target.value })} placeholder="es. ufficio@azienda.it, titolare@azienda.it" /><p className="text-[11px] text-muted-foreground">Separate da virgola (max 10). Ricevono le stesse notifiche delle pratiche dell'email comunicazioni.</p></div>}
              <div className="space-y-1.5"><Label>{editing ? "Nuova password (lascia vuoto per non cambiare)" : "Password"}</Label><Input data-testid="user-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Livello di accesso</Label>
                {isAdmin ? (
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Amministratore</SelectItem>
                    <SelectItem value="collaboratore">Collaboratore Interno</SelectItem>
                    <SelectItem value="convenzionato">Convenzionato</SelectItem>
                    <SelectItem value="segnalatore">Segnalatore</SelectItem>
                    <SelectItem value="istituto">Istituto Finanziario</SelectItem>
                  </SelectContent>
                </Select>
                ) : (
                  <div className="rounded-md border border-border px-3 py-2 text-sm bg-muted/40" data-testid="user-role-locked">Convenzionato</div>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Sede</Label>
                <Select value={form.sede_id} onValueChange={(v) => setForm({ ...form, sede_id: v })}>
                  <SelectTrigger data-testid="user-sede-select"><SelectValue placeholder="Nessuna sede" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessuna sede</SelectItem>
                    {sedi.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {(form.role === "convenzionato" || form.role === "segnalatore") && (
                <div className="space-y-1.5">
                  <Label>Collaboratore assegnato</Label>
                  {isAdmin ? (
                  <SearchableSelect value={form.referente_id} onValueChange={(v) => setForm({ ...form, referente_id: v })}
                    testid="user-referente-select" placeholder="Nessun collaboratore" searchPlaceholder="Cerca collaboratore…"
                    options={[{ value: "none", label: "Nessun collaboratore" }, ...users.filter((u) => u.role === "collaboratore" && u.active !== false).map((u) => ({ value: u.id, label: u.name }))]} />
                  ) : (
                  <div className="rounded-md border border-border px-3 py-2 text-sm bg-muted/40" data-testid="user-referente-locked">
                    {(!editing || !form.referente_id || form.referente_id === "none" || form.referente_id === me?.id) ? `${me?.name} (tu)` : "Assegnato ad un altro collaboratore"}
                  </div>
                  )}
                </div>
              )}
              <div className="border-t border-border pt-3 mt-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Anagrafica / Contatti</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Cellulare</Label><Input data-testid="user-cellulare-input" value={form.cellulare} onChange={(e) => setForm({ ...form, cellulare: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Telefono fisso</Label><Input data-testid="user-telefono-fisso-input" value={form.telefono_fisso} onChange={(e) => setForm({ ...form, telefono_fisso: e.target.value })} /></div>
                  {isAdmin && editing && ["admin", "collaboratore"].includes(form.role) && (
                    <div className="space-y-1.5"><Label>Interno centralino (Romulus)</Label><Input data-testid="user-romulus-ext-input" placeholder="es. 101" value={form.romulus_extension || ""} onChange={(e) => setForm({ ...form, romulus_extension: e.target.value })} /></div>
                  )}

                  <div className="space-y-1.5"><Label>Sede operativa</Label><Input data-testid="user-citta-input" value={form.citta_residenza} onChange={(e) => setForm({ ...form, citta_residenza: e.target.value })} /></div>
                  <div className="space-y-1.5"><Label>Provincia</Label><Input data-testid="user-provincia-input" value={form.provincia} maxLength={2} onChange={(e) => setForm({ ...form, provincia: e.target.value.toUpperCase() })} placeholder="Es. LT" /></div>
                  <div className="space-y-1.5"><Label>Codice convenzione</Label><Input data-testid="user-codice-convenzione-input" value={form.codice_convenzione} onChange={(e) => setForm({ ...form, codice_convenzione: e.target.value })} /></div>
                  <div className="space-y-1.5 col-span-2"><Label>Utenze finanziarie</Label><Input data-testid="user-utenze-finanziarie-input" value={form.utenze_finanziarie} onChange={(e) => setForm({ ...form, utenze_finanziarie: e.target.value })} placeholder="Es. istituti/convenzioni gestite" /></div>
                  {isAdmin && form.role !== "istituto" && <div className="space-y-1.5 col-span-2"><Label>% provvigione in uscita (default)</Label><Input data-testid="user-percentuale-provvigione-input" type="number" min="0" max="100" step="0.01" value={form.percentuale_provvigione} onChange={(e) => setForm({ ...form, percentuale_provvigione: e.target.value })} placeholder="Es. 20" /><p className="text-[11px] text-muted-foreground">Calcolata sul totale delle provvigioni in entrata della pratica. L'importo viene proposto e resta modificabile.</p></div>}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button data-testid="user-submit-btn" onClick={save}>{editing ? "Salva" : "Crea"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3" data-testid="users-filters">
        <Input className="w-56" data-testid="users-search-input" placeholder="Cerca nome, email, telefono…" value={q} onChange={(e) => setQ(e.target.value)} />
        <Select value={fRole} onValueChange={setFRole}>
          <SelectTrigger className="w-44" data-testid="filter-role-select"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i tipi</SelectItem>
            <SelectItem value="admin">Amministratore</SelectItem>
            <SelectItem value="collaboratore">Collaboratore Interno</SelectItem>
            <SelectItem value="convenzionato">Convenzionato</SelectItem>
            <SelectItem value="segnalatore">Segnalatore</SelectItem>
            <SelectItem value="istituto">Istituto Finanziario</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="w-40" data-testid="filter-status-select"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="active">Attivi</SelectItem>
            <SelectItem value="disabled">Disattivati</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fSede} onValueChange={setFSede}>
          <SelectTrigger className="w-44" data-testid="filter-sede-select"><SelectValue placeholder="Sede" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le sedi</SelectItem>
            <SelectItem value="none">Senza sede</SelectItem>
            {sedi.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}
          </SelectContent>
        </Select>
        <SearchableSelect value={fColl} onValueChange={setFColl} className="w-52" testid="filter-collaboratore-select"
          placeholder="Tutti i collaboratori" searchPlaceholder="Cerca collaboratore…"
          options={[{ value: "all", label: "Tutti i collaboratori" }, { value: "none", label: "Senza collaboratore" },
            ...users.filter((u) => u.role === "collaboratore").map((u) => ({ value: u.id, label: u.name }))]} />
        {(fRole !== "all" || fStatus !== "all" || fSede !== "all" || fColl !== "all" || q) && (
          <Button variant="ghost" size="sm" data-testid="clear-filters-btn" onClick={() => { setFRole("all"); setFStatus("all"); setFSede("all"); setFColl("all"); setQ(""); }}>Azzera filtri</Button>
        )}
      </div>

      {isAdmin && sel.length > 0 && (
        <Card className="p-3 flex flex-wrap items-center gap-3" data-testid="users-bulk-bar">
          <span className="text-sm font-medium">{sel.length} utenti selezionati</span>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="destructive" data-testid="users-bulk-delete-btn" onClick={() => setBulkDelOpen(true)}><Trash2 className="h-4 w-4 mr-2" />Elimina selezionati</Button>
            <Button variant="ghost" data-testid="users-bulk-cancel" onClick={() => setSel([])}>Annulla</Button>
          </div>
        </Card>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="user-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-3 w-8">{isAdmin && <input type="checkbox" className="accent-primary" checked={allSel} onChange={toggleAllSel} data-testid="users-select-all" />}</th>
                <SortHead label="Nome" k="name" />
                <SortHead label="Accesso / Email comunicazioni" k="email" />
                <SortHead label="Livello" k="role" />
                <SortHead label="Sede" k="sede" />
                <SortHead label="Ultimo accesso" k="last_login" />
                <SortHead label="Stato" k="status" />
                <th className="px-4 py-3 font-semibold text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((u) => {
                const Icon = ROLE_ICON[u.role] || Contact;
                return (
                  <tr key={u.id} className="border-b border-border last:border-0">
                    <td className="px-3 py-3">{isAdmin && u.id !== me.id && <input type="checkbox" className="accent-primary" checked={sel.includes(u.id)} onChange={() => toggleSel(u.id)} data-testid={`user-check-${u.id}`} />}</td>
                    <td className="px-4 py-3 font-medium">{u.name}{u.id === me.id && <span className="ml-2 text-[10px] text-primary">(tu)</span>}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <div className="font-mono text-xs" data-testid={`user-login-${u.id}`}>{u.email}</div>
                      {u.email_comunicazioni && u.email_comunicazioni !== u.email && <div className="text-[11px]">✉ {u.email_comunicazioni}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span data-testid={`user-role-badge-${u.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-0.5 text-xs font-semibold">
                        <Icon className="h-3.5 w-3.5" />{ROLE_LABELS[u.role]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {sedeName(u.sede_id)}
                      {u.referente_id && <div className="text-[11px]">Collab.: {userName(u.referente_id) || "—"}</div>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap" data-testid={`user-last-login-${u.id}`}><LastLogin iso={u.last_login_at} /></td>
                    <td className="px-4 py-3">
                      {u.active !== false
                        ? <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" />Attivo</span>
                        : <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Ban className="h-3.5 w-3.5" />Disattivato</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" data-testid={`edit-user-btn-${u.id}`} onClick={() => openEdit(u)}><Pencil className="h-4 w-4" /></Button>
                        {u.id !== me.id && (
                          <Button variant="ghost" size="icon" data-testid={`toggle-user-btn-${u.id}`} onClick={() => toggleActive(u)}>
                            {u.active !== false ? <Ban className="h-4 w-4 text-destructive" /> : <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                          </Button>
                        )}
                        {isAdmin && u.id !== me.id && (
                          <Button variant="ghost" size="icon" data-testid={`delete-user-btn-${u.id}`} onClick={() => setDelUser(u)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">Nessun utente corrisponde ai filtri</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <AlertDialog open={!!delUser} onOpenChange={(o) => !o && setDelUser(null)}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l'utente?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare definitivamente <strong>{delUser?.name}</strong> ({delUser?.email}). L'azione è irreversibile. Se vuoi solo bloccare l'accesso, usa "Disattiva".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-user-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-user-confirm" onClick={confirmDelete}>Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDelOpen} onOpenChange={setBulkDelOpen}>
        <AlertDialogContent data-testid="users-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {sel.length} utenti?</AlertDialogTitle>
            <AlertDialogDescription>Stai per eliminare definitivamente {sel.length} utenti selezionati. L'azione è irreversibile.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="users-bulk-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="users-bulk-delete-confirm" onClick={bulkDelete}>Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {me?.role === "admin" && <AccessLogs />}
    </div>
  );
}
